import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ChevronLeft, Clock, Store, Star, Truck } from "lucide-react";
import { api, imageUrl } from "../lib/api";
import { LiveBadge } from "../components/LiveBadge";
import { useI18n } from "../i18n";
import { MIN_REVIEWS } from "../context/CityContext";
import { usePageTitle } from "../lib/usePageTitle";

export default function VendorList() {
  const { city } = useParams();
  const decoded = decodeURIComponent(city);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const scheduleVendors = useMemo(
    () => vendors.filter((v) => v.today_hours || v.opening_hours).slice(0, 3),
    [vendors]
  );
  const navigate = useNavigate();
  const { t } = useI18n();
  usePageTitle(
    `Livraison CBD ${decoded} — STUFF CBD | Fleurs, résines, e-liquides`,
    `Livraison rapide de CBD premium à ${decoded} : fleurs, résines, e-liquides, puffs, chicha. Chanvre légal <0,3% THC. Paiement cash à la livraison. 18+.`
  );

  useEffect(() => {
    setLoading(true);
    api.get(`/vendors?city=${encodeURIComponent(decoded)}`)
      .then((r) => setVendors(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [decoded]);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
      <button data-testid="back-to-cities" onClick={() => navigate("/")} className="flex items-center gap-1 text-zinc-400 hover:text-neon-green text-sm uppercase font-semibold mb-6 transition-colors">
        <ChevronLeft className="h-4 w-4" /> {t("vlist.change")}
      </button>

      <div className="mb-8">
        <p className="text-neon-magenta font-graffiti text-2xl">{t("vlist.in")}</p>
        <h1 className="font-anton text-4xl sm:text-5xl uppercase text-white">{decoded}</h1>
      </div>

      {/* Indicateur de statut livraison */}
      {!loading && vendors.length > 0 && (
        vendors.some((v) => v.is_live) ? (
          <div data-testid="city-status-open" className="flex items-center gap-3 border border-neon-green/50 bg-neon-green/5 px-5 py-3 mb-6">
            <span className="relative flex h-3 w-3 shrink-0"><span className="animate-ping absolute h-full w-full rounded-full bg-neon-green opacity-75" /><span className="relative rounded-full h-3 w-3 bg-neon-green" /></span>
            <p className="text-neon-green font-bold uppercase text-sm">{t("vlist.open")}</p>
          </div>
        ) : (
          <div data-testid="city-status-closed" className="border border-red-500/50 bg-red-500/5 px-5 py-3 mb-6">
            <p className="text-red-400 font-bold uppercase text-sm flex items-center gap-2"><span className="h-3 w-3 rounded-full bg-red-500 shrink-0" /> {t("vlist.closed")}</p>
            <p className="text-zinc-400 text-xs mt-1">{t("vlist.preorder")}</p>
            {scheduleVendors.map((v) => (
              <p key={v.id} className="text-zinc-500 text-xs mt-0.5">🕑 {v.name} : {v.today_hours || v.opening_hours}</p>
            ))}
          </div>
        )
      )}

      {loading ? (
        <p className="text-zinc-500">{t("vlist.loading")}</p>
      ) : vendors.length === 0 ? (
        <div className="bg-ink-surface border border-zinc-800 p-10 text-center">
          <Store className="h-10 w-10 text-zinc-600 mx-auto mb-3" />
          <p className="text-zinc-400 mb-4">{t("vlist.none")}</p>
          <button
            data-testid="empty-city-national-cta"
            onClick={() => navigate("/boutique/national")}
            className="inline-flex items-center gap-2 bg-neon-green text-black font-anton uppercase px-6 py-3 glow-green hover:bg-neon-magenta hover:text-white transition-colors mb-4"
          >
            <Truck className="h-5 w-5" /> {t("vlist.national48")}
          </button>
          <p><Link to="/recrutement" className="text-neon-green font-semibold uppercase text-sm">{t("vlist.first")}</Link></p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {vendors.map((v) => (
            <button
              key={v.id}
              data-testid="vendor-card"
              onClick={() => navigate(`/boutique/${v.id}`)}
              className="flex items-center gap-4 bg-ink-surface border border-zinc-800 p-5 text-left hover:border-neon-magenta hover:shadow-[0_0_15px_rgba(192,38,211,0.2)] transition-all"
            >
              <div className="h-16 w-16 rounded-full overflow-hidden border-2 border-zinc-700 shrink-0 bg-black flex items-center justify-center">
                {v.avatar_url ? (
                  <img src={imageUrl(v.avatar_url)} alt={v.name} className="h-full w-full object-cover" />
                ) : (
                  <span className="font-anton text-2xl text-neon-green">{v.name?.[0]?.toUpperCase()}</span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-anton text-xl uppercase text-white truncate">{v.name}</h3>
                <div className="flex items-center gap-3 mt-1">
                  <LiveBadge live={v.is_live} />
                  <span className="flex items-center gap-1 text-zinc-500 text-xs"><Clock className="h-3 w-3" /> {v.delivery_time || "< 2h"}</span>
                  {v.rating_count >= MIN_REVIEWS ? (
                    <span className="flex items-center gap-1 text-xs text-neon-green font-bold" data-testid="vendor-rating"><Star className="h-3 w-3 fill-neon-green" /> {v.rating} · {v.rating_count} {t("vlist.verified")}</span>
                  ) : v.is_national ? null : (
                    <span data-testid="new-vendor-badge" className="text-[10px] uppercase font-bold border border-neon-magenta text-neon-magenta px-1.5 py-0.5">✨ {t("product.newVendor")}</span>
                  )}
                </div>
                {v.vacation_active ? (
                  <p data-testid="vendor-vacation" className="text-amber-400 text-xs mt-1 font-bold">🏖️ {t("st.vacation")} {(v.vacation_until || "").split("-").reverse().join("/")}</p>
                ) : v.today_off ? (
                  <p data-testid="vendor-rest-today" className="text-zinc-500 text-xs mt-1">😴 {t("st.restToday")}</p>
                ) : v.today_hours ? (
                  <p data-testid="vendor-today-hours" className="text-zinc-500 text-xs mt-1">🕑 {t("st.today")} : {v.today_hours}</p>
                ) : null}
                {v.description ? <p className="text-zinc-500 text-xs mt-1 line-clamp-1">{v.description}</p> : null}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
